import MuiBox from '@mui/material/Box';
import MuiCheckbox from '@mui/material/Checkbox';
import MuiFormControlLabel from '@mui/material/FormControlLabel';
import { alpha, type Theme } from '@mui/material/styles';
import { useId, type ReactNode } from 'react';
import { Text } from '../Text';

export interface CheckboxProps {
  checked: boolean;
  // Emits the next checked state, never MUI's synthetic event — same idiom as
  // TextField emitting the string value.
  onChange: (checked: boolean) => void;
  // Real label: FormControlLabel makes the whole row (box + text) the click area
  // and the accessible name, so no caller ever pairs a bare box with loose text.
  label: ReactNode;
  // A help line under the label, announced as the box's DESCRIPTION rather than folded into its
  // name: inside `label` it would be read as part of what the box is called on every pass. Phrasing
  // content only — it renders inside a paragraph. Muted on a plain row; inside `highlight` the
  // warning wash pulls muted below AA (≈4.36:1), so there it takes the primary ink.
  description?: ReactNode;
  disabled?: boolean;
  // Draws the emphasized opt-in container around the control: 8px surface radius
  // (the one Dialog/Alert/Disclosure share), hairline warning border, pale warning
  // wash. Lives here because no view can compute the alpha tint without importing
  // @mui/material/styles, which the façade lint forbids outside this package.
  highlight?: boolean;
  // Focus the box on mount: for a dialog whose first job is a choice, so the keyboard starts there
  // instead of on the dialog's container. One box per screen at most.
  autoFocus?: boolean;
}

const highlightSx = (theme: Theme) => ({
  border: '1px solid',
  borderColor: alpha(theme.palette.warning.main, 0.5),
  backgroundColor: alpha(theme.palette.warning.main, 0.08),
  borderRadius: 2,
  px: 2,
  py: 0.5,
  width: '100%',
});

// Where the label text starts, so the description lines up under it rather than under the box:
// the box's 42px hit area minus the -11px FormControlLabel pulls it back by.
const DESCRIPTION_INSET_PX = 31;

export function Checkbox({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  highlight = false,
  autoFocus = false,
}: CheckboxProps) {
  // Per instance, so a list of boxes never shares (and so never cross-wires) a description id.
  const descriptionId = useId();
  const hasDescription = description !== undefined;

  const control = (
    <MuiFormControlLabel
      control={
        <MuiCheckbox
          checked={checked}
          autoFocus={autoFocus}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
          {...(hasDescription ? { inputProps: { 'aria-describedby': descriptionId } } : {})}
        />
      }
      label={label}
      disabled={disabled}
    />
  );

  // Always in its own box, never the bare label: a spaced Stack resets the margin of its direct
  // children, which would undo the -11px FormControlLabel uses to line the box up with the text
  // above it. The description lives OUTSIDE the <label>: anything inside it is part of the
  // accessible name.
  const row = (
    <MuiBox sx={{ display: 'flex', flexDirection: 'column' }}>
      {control}
      {hasDescription && (
        <MuiBox sx={{ pl: `${String(DESCRIPTION_INSET_PX)}px` }}>
          <Text variant="body2" color={highlight ? 'primary' : 'muted'} id={descriptionId}>
            {description}
          </Text>
        </MuiBox>
      )}
    </MuiBox>
  );

  if (!highlight) return row;

  return <MuiBox sx={highlightSx}>{row}</MuiBox>;
}
